import { fetchJSON as fetchAPI } from './client';

export type PlanningHorizon = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'rolling';
export type PlanningDomain = 'demand' | 'sales' | 'inventory' | 'procurement' | 'supply' | 'warehouse' | 'financial' | 'executive' | 'automation' | 'sop';
export type PlanStatus = 'draft' | 'pending' | 'approved' | 'published' | 'completed' | 'cancelled' | 'archived';
export type ScenarioType = 'baseline' | 'best_case' | 'worst_case' | 'optimistic' | 'conservative' | 'custom';

export type PlanningWorkspace = {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  fiscal_year: number;
  status?: string;
  owner_id?: string;
  cycle_count: number;
  version_count: number;
  created_at: string;
  updated_at: string;
};

export type PlanningCycle = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  name: string;
  horizon: PlanningHorizon;
  domain: PlanningDomain;
  status: PlanStatus;
  start_date: string;
  end_date: string;
  version_count: number;
  created_at: string;
};

export type PlanningVersion = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  cycle_id: string;
  name: string;
  version: number;
  domain: PlanningDomain;
  horizon: PlanningHorizon;
  status: PlanStatus;
  is_baseline: boolean;
  scenario_id?: string;
  created_by?: string;
  approved_by?: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
};

export type PlanningScenario = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  version_id?: string;
  name: string;
  type: ScenarioType;
  description?: string;
  is_baseline: boolean;
  kpi_count: number;
  created_at: string;
};

export type PlanningAssumption = {
  id: string;
  tenant_id: string;
  version_id: string;
  category: string;
  statement: string;
  value?: string;
  source?: string;
  confidence: number;
  status?: string;
  created_at: string;
};

export type PlanningKPI = {
  id: string;
  tenant_id: string;
  version_id: string;
  scenario_id?: string;
  metric_key: string;
  label: string;
  target: number;
  actual?: number;
  unit?: string;
};

export type PlanningTask = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  cycle_id?: string;
  version_id?: string;
  title: string;
  description?: string;
  assignee_id?: string;
  status: string;
  due_date?: string;
  created_at: string;
};

export type PlanningApproval = {
  id: string;
  tenant_id: string;
  version_id: string;
  requested_by?: string;
  approver_id?: string;
  status: string;
  comment?: string;
  requested_at: string;
  decided_at?: string;
};

export type PlanningBaseline = {
  id: string;
  tenant_id: string;
  cycle_id: string;
  version_id: string;
  scenario_id?: string;
  approved_at: string;
  approved_by?: string;
};

export type PlanningCalendar = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  name: string;
  fiscal_year: number;
  start_month: number;
  granularity: string;
  period_count: number;
  rolling_weeks?: number;
};

export type PlanningModel = {
  id: string;
  tenant_id: string;
  module_key: string;
  name: string;
  domain: PlanningDomain;
  description?: string;
  api_endpoint?: string;
  enabled: boolean;
  version?: string;
};

export type PlanningDashboard = {
  tenant_id: string;
  workspace_id?: string;
  active_cycles: number;
  active_versions: number;
  pending_approvals: number;
  open_tasks: number;
  active_scenarios: number;
  registered_modules: number;
  baseline_count: number;
  generated_at: string;
};

export type ScenarioComparison = {
  version_id: string;
  scenarios: PlanningScenario[];
  kpis_by_scenario: Record<string, PlanningKPI[]>;
  generated_at: string;
};

export type ExtensionPoint = {
  key: string;
  name: string;
  domain: PlanningDomain;
  description: string;
  hook_events?: string[];
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_PLANNING_API ?? '/api/v1/planning';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const planningApi = {
  dashboard: () => fetchJSON<PlanningDashboard>('/dashboard'),
  getWorkspace: () => fetchJSON<PlanningWorkspace>('/workspace'),
  listCycles: (workspaceId?: string) =>
    fetchJSON<PlanningCycle[]>(`/cycles${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  listVersions: (workspaceId?: string) =>
    fetchJSON<PlanningVersion[]>(`/versions${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  getVersion: (id: string) => fetchJSON<PlanningVersion>(`/versions/${id}`),
  submitVersion: (id: string) =>
    fetchJSON<PlanningVersion>(`/versions/${id}/submit`, { method: 'POST' }),
  publishVersion: (id: string) =>
    fetchJSON<PlanningVersion>(`/versions/${id}/publish`, { method: 'POST' }),
  listScenarios: (workspaceId?: string) =>
    fetchJSON<PlanningScenario[]>(`/scenarios${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  compareScenarios: (versionId: string) =>
    fetchJSON<ScenarioComparison>('/scenarios/compare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version_id: versionId }),
    }),
  listAssumptions: (versionId?: string) =>
    fetchJSON<PlanningAssumption[]>(`/assumptions${versionId ? `?version_id=${versionId}` : ''}`),
  listKPIs: (versionId?: string) =>
    fetchJSON<PlanningKPI[]>(`/kpis${versionId ? `?version_id=${versionId}` : ''}`),
  listTasks: (workspaceId?: string) =>
    fetchJSON<PlanningTask[]>(`/tasks${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  listApprovals: () => fetchJSON<PlanningApproval[]>('/approvals'),
  decideApproval: (id: string, approved: boolean) =>
    fetchJSON<PlanningApproval>(`/approvals/${id}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved }),
    }),
  listBaselines: () => fetchJSON<PlanningBaseline[]>('/baselines'),
  getCalendar: (workspaceId?: string) =>
    fetchJSON<PlanningCalendar>(`/calendar${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  listModules: () => fetchJSON<PlanningModel[]>('/modules'),
  extensionPoints: () => fetchJSON<ExtensionPoint[]>('/extensions'),
  listActivity: (limit = 20) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),
};
